/* Playtest board — "Board D · Playmat", driven by the headless engine.
 *
 * The mockup drew a felt table with two playmats on it: named zones, a life
 * stack you can count, a fanned hand, and one rail on the right holding the
 * selected card, the phase and the log. It drew a board with nothing happening
 * to it. This is that board wired to a real two-sided game — the same engine
 * the batch simulator runs — so every number is read and every click is an
 * engine action.
 *
 * Two things the mockup had no way to show, and where they went:
 *
 *   Prompts (block, counter, trigger, targets) sit in flow above the mat
 *   rather than floating over it, so the mat rescales and nothing is covered
 *   at the moment you most need to see it.
 *
 *   Per-unit actions — attack, attach DON!!, activate an ability — live in the
 *   inspector rather than as buttons under every card. The mockup's mat is
 *   clean, and that is the better model: select a thing, then act on it.
 */

import { h, fill, toast, fmt, announce } from './dom.js';
import { cardTile, costLabel } from './card.js';
import { getCards, withStubs } from '../data/cards.js';
import { isLegal, totalCards } from '../data/deck.js';
import { archetypes, archetypeDeck } from '../data/meta.js';
import {
  createGame, apply, legalActions, view, canAttack, attackTargets,
  leaderAbilityStatus, charAbilityStatus, costOf, PHASES, PHASE_LABEL, unitName,
} from '../engine/engine.js';
import { decide, SKILL } from '../engine/ai.js';
import { buildScenario, goalStatus, pressure } from '../engine/scenario.js';
import {
  loadScenarios, getScenarios, scenariosError, missingCards, scenarioById,
  isSolved, markSolved, currentScenarioId, setCurrentScenarioId,
} from '../data/scenarios.js';
import { explain, RULES, ruleFor } from './explain.js';

const HUMAN = 'p1';
const AI = 'p2';

/* The mat is authored at one width and scaled to whatever the pane has. */
const MAT_W = 1010;

/* How small the mat is allowed to get before scrolling beats shrinking. At
 * this scale a card is ~52px wide, which is about the last point its cost and
 * power are still readable. Short windows scroll rather than producing a
 * technically-fitted board nobody can play on. */
const MIN_FIT = 0.62;

/* ---------------------------------------------------------------------- */
/* scenarios                                                               */
/* ---------------------------------------------------------------------- */

/**
 * The goal, in words, for the player who has to achieve it.
 *
 * Exported because it is the one part of the scenario UI with a right and a
 * wrong answer: `GOAL_KINDS` in the engine and this table have to agree, and a
 * test asserts they do. A goal the board cannot describe is a puzzle the
 * player is asked to solve without being told what winning is.
 */
export function goalLabel(goal) {
  switch (goal?.kind) {
    case 'winThisTurn': return 'Win this turn.';
    case 'lifeAtMost':
      return goal.life === 0
        ? 'Take their last Life this turn.'
        : `Get them down to ${goal.life} Life this turn.`;
    case 'koAll': return 'K.O. every Character they control, this turn.';
    case 'survive':
      return `Survive ${goal.turns} turn${goal.turns === 1 ? '' : 's'} with at least ${goal.lifeAtLeast} Life.`;
    default: return 'Reach the goal.';
  }
}

/* Difficulty as something you can see at a glance, and something a screen
 * reader can say. Both, not one or the other. */
function difficultyDots(n) {
  return h('span.scdiff', {
    role: 'img',
    'aria-label': `difficulty ${n} of 5`,
  }, '●'.repeat(n) + '○'.repeat(5 - n));
}

export function createBoard(app) {
  let G = null;
  let selected = null;          // { kind:'hand'|'char'|'leader', index?, uid? }
  let attacker = null;          // ref of the unit we're attacking with
  let beginner = true;
  let helpOpen = false;
  let explainIdx = -1;
  let explainOpen = false;
  let skill = SKILL.solid;
  let pendingAiTimer = null;
  let announcedPrompt = null;   // so an open prompt is announced once, not per render

  /* Scenario play. `scenario` null means this is a free game — everything
   * below branches on that one field rather than on a mode enum, so a board
   * with no puzzle attached behaves exactly as it did before. */
  let scenario = null;
  let scenarioStatus = 'open';  // 'open' | 'solved' | 'failed'
  let hint = null;              // the last nudge asked for, cleared on retry
  let setupMode = 'free';       // which half of the setup screen is showing
  let scenariosRequested = false;
  let scenariosLoaded = false;

  const els = {};

  /* ------------------------------------------------------------------ */
  /* game control                                                        */
  /* ------------------------------------------------------------------ */

  function startGame({ opponentKey, first, seed } = {}) {
    if (!isLegal(app.deck)) {
      toast('Your deck must be legal (a Leader and exactly 50 cards) to playtest.', 'bad');
      app.go('builder');
      return;
    }
    const field = archetypes();
    const opp = field.find(a => String(a.id) === String(opponentKey)) || field[0];
    if (!opp) {
      toast('No meta decks available — generate data/meta.json first.', 'bad');
      return;
    }
    const oppDeck = archetypeDeck(opp, 'consensus');

    // Stub any id the card dump lacks so a missing printing can't abort a game.
    // The stubs live on a view of the database, not in it, so they can't follow
    // the player back to the builder.
    const { cards, missing } = withStubs([
      oppDeck.leader, ...Object.keys(oppDeck.cards),
      app.deck.leader, ...Object.keys(app.deck.cards),
    ]);
    if (missing.length) toast(`${missing.length} card(s) missing from the card data — standing in as vanilla bodies.`, 'bad');

    G = createGame({
      cards,
      seed: seed ?? (Date.now() % 100000),
      firstPlayer: first || 'p1',
      p1: { name: app.deck.name || 'Your deck', leaderId: app.deck.leader, deck: { ...app.deck.cards } },
      p2: { name: opp.name, leaderId: oppDeck.leader, deck: oppDeck.cards },
    });
    scenario = null;
    clearTable();
    render();
    pumpAI();
  }

  /* Everything about the table that is not the game itself. Called from both
   * ways in — a scenario retry has to forget the selection and the open
   * explanation as thoroughly as a new game does. */
  function clearTable() {
    selected = null; attacker = null; explainIdx = -1; explainOpen = false;
    scenarioStatus = 'open';
    hint = null;
    announcedPrompt = null;
  }

  /* ------------------------------------------------------------------ */
  /* scenario play                                                       */
  /* ------------------------------------------------------------------ */

  /**
   * Load a puzzle onto the table.
   *
   * The card database goes in whole and unstubbed. `startGame` stands missing
   * printings up as vanilla bodies so a game can't be aborted by a gap in the
   * dump; a puzzle must not do that, because the line that solves it was
   * proved against printed text and a vanilla stand-in can quietly delete it.
   * A puzzle we cannot build honestly is one we decline to offer.
   */
  function startScenario(sc) {
    const db = getCards();
    const missing = missingCards(sc, db);
    if (missing.length) {
      toast(`This puzzle needs card data we don't have: ${missing.join(', ')}.`, 'bad');
      return;
    }
    try {
      G = buildScenario(sc, db);
    } catch (err) {
      toast(`Could not set that puzzle up: ${err.message}`, 'bad');
      return;
    }
    clearTimeout(pendingAiTimer);
    scenario = sc;
    /* Fixed, not the difficulty select. The opponent's answers are the AI's,
     * and `npm run scenarios` proves every line against Solid — a Casual
     * defender could decline a block the solution assumes, and a puzzle whose
     * answer depends on the opponent misplaying teaches the wrong thing. */
    skill = SKILL.solid;
    clearTable();
    setCurrentScenarioId(sc.id);
    render();
    pumpAI();
  }

  function retryScenario() {
    if (scenario) startScenario(scenario);
  }

  /** Back to the list, and stop this puzzle claiming the tab on reload. */
  function leaveScenario() {
    clearTimeout(pendingAiTimer);
    setCurrentScenarioId(null);
    setupMode = 'scenarios';
    scenario = null;
    G = null;
    clearTable();
    render();
  }

  /**
   * Score the position. Cheap, pure, and called after every applied action.
   *
   * Solving is recorded here rather than in the banner that announces it:
   * render() runs for reasons that have nothing to do with the game — a log
   * click, the teach checkbox — and marking progress from a render is how a
   * puzzle gets re-solved on every repaint.
   */
  function syncGoal() {
    if (!scenario || !G) return 'open';
    const was = scenarioStatus;
    scenarioStatus = goalStatus(G, scenario, { you: HUMAN });
    if (scenarioStatus === 'solved' && was !== 'solved') {
      markSolved(scenario.id);
      setCurrentScenarioId(null);
      announce(`Solved: ${scenario.name}.`);
    }
    return scenarioStatus;
  }

  /** Submit a human action, then let the AI take its turn. */
  function act(action) {
    if (!G || G.over) return;
    if (scenario && scenarioStatus !== 'open') return;
    apply(G, action);
    selected = null;
    syncGoal();
    render();
    pumpAI();
  }

  /* The AI runs on a short timer so its moves are legible rather than
   * appearing all at once — the state is already computed, this is pacing. */
  function pumpAI() {
    clearTimeout(pendingAiTimer);
    if (!G || G.over) { syncGoal(); render(); return; }
    /* A decided puzzle stops here. "Win this turn" fails the moment the turn
     * passes, and watching the opponent play out a turn you have already lost
     * to is not the lesson — the answer is Retry, immediately and for free. */
    if (scenario && syncGoal() !== 'open') { render(); return; }
    const needsAI = G.pending ? G.pending.side === AI : G.active === AI;
    if (!needsAI) { render(); return; }

    pendingAiTimer = setTimeout(() => {
      /* The game can be torn down between scheduling and firing — the New game
       * button sets `G = null` without clearing this timer — and decide(null)
       * throws inside a timer callback, where nothing catches it. */
      if (!G || G.over) { syncGoal(); render(); return; }
      const a = decide(G, skill);
      if (!a) { render(); return; }
      apply(G, a);
      syncGoal();
      render();
      pumpAI();
    }, 420);
  }

  /* ------------------------------------------------------------------ */
  /* setup screen                                                        */
  /* ------------------------------------------------------------------ */

  /**
   * The pre-game screen, which is now two screens.
   *
   * Free play and scenarios are the same board with different starting
   * positions, so they share a tab rather than adding a fourth to the header —
   * and the switch is a pair of buttons rather than a second tablist, which
   * `src/main.js` would then have to know about.
   */
  function renderSetup() {
    return h('div.pane', { style: { display: 'flex', justifyContent: 'center' } },
      h('div', { style: { width: '100%', maxWidth: '740px' } },
        h('div.scmodes', { role: 'group', 'aria-label': 'Playtest mode' },
          modeButton('free', 'Free play'),
          modeButton('scenarios', 'Scenarios'),
        ),
        setupMode === 'scenarios' ? renderScenarioPicker() : renderFreeSetup(),
      ),
    );
  }

  function modeButton(key, label) {
    const on = setupMode === key;
    return h('button.scmode', {
      type: 'button',
      class: on ? 'is-on' : '',
      'aria-pressed': String(on),
      onclick: () => { setupMode = key; if (key === 'scenarios') ensureScenarios(); refreshSetup(); },
    }, label);
  }

  function renderFreeSetup() {
    const field = archetypes();
    const legal = isLegal(app.deck);

    return h('div', null,
        h('div.panel', { style: { padding: '20px' } },
          h('h2', { style: { margin: '0 0 4px', font: '600 17px/1 var(--font)' } }, 'New playtest'),
          h('div', { style: { font: '400 12px/1.6 var(--font)', color: 'var(--tx-4)', marginBottom: '18px' } },
            'A real two-sided game against a meta deck, using the same engine as the matchup simulator.'),

          !legal ? h('div.banner', { style: { marginBottom: '16px' } },
            h('div.live'),
            h('div', `Your deck isn't legal yet — ${totalCards(app.deck)}/50 with ${app.deck.leader ? 'a Leader' : 'no Leader'}.`),
            h('button.btn', { style: { marginLeft: 'auto' }, onclick: () => app.go('builder') }, 'Open builder'),
          ) : null,

          h('div.eyebrow', { style: { marginBottom: '9px' } }, 'Opponent'),
          !field.length
            ? h('div.empty-note', 'No meta snapshot loaded. Run `npm run meta` to generate data/meta.json.')
            : h('div', {
              style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: '9px', marginBottom: '18px' },
            },
              field.map(a => {
                const on = els.oppKey === String(a.id);
                return h('button.btn', {
                  style: {
                    textAlign: 'left', padding: '11px 13px', lineHeight: '1.4',
                    borderColor: on ? 'var(--accent)' : undefined,
                    background: on ? 'rgba(91,157,255,.12)' : undefined,
                  },
                  onclick: () => { els.oppKey = String(a.id); refreshSetup(); },
                },
                  h('div', { style: { font: '600 13px/1.3 var(--font)', color: 'var(--tx)' } }, a.name),
                  h('div', { style: { font: '500 10px/1.4 var(--mono)', color: 'var(--tx-4)' } },
                    `${(a.share * 100).toFixed(1)}% of field · ${a.deckCount} lists`),
                );
              })),

          /* Render these from state, not from a hardcoded default.
           *
           * refreshSetup() replaces the whole pane on every opponent click, so
           * `selected: true` on Solid and on "first" snapped both selects back
           * visually while `skill` and `els.first` kept the real values — the
           * screen and the game then disagreed about what was about to be
           * played, which is worse than either being wrong alone. */
          h('div.row', { style: { gap: '14px', flexWrap: 'wrap' } },
            labelled('Difficulty', h('select.input', {
              style: { width: 'auto' },
              onchange: e => { skill = +e.target.value; },
            },
              h('option', { value: SKILL.casual, selected: skill === SKILL.casual }, 'Casual'),
              h('option', { value: SKILL.solid, selected: skill === SKILL.solid }, 'Solid'),
              h('option', { value: SKILL.sharp, selected: skill === SKILL.sharp }, 'Sharp'),
            )),
            labelled('You go', h('select.input', {
              style: { width: 'auto' },
              onchange: e => { els.first = e.target.value; },
            },
              h('option', { value: 'p1', selected: (els.first || 'p1') === 'p1' }, 'first'),
              h('option', { value: 'p2', selected: els.first === 'p2' }, 'second'),
            )),
            h('div.spacer'),
            h('button.btn-pri', {
              style: { padding: '11px 20px' },
              disabled: !legal || !field.length,
              onclick: () => startGame({ opponentKey: els.oppKey ?? String(field[0]?.id), first: els.first || 'p1' }),
            }, 'Start game'),
          ),
        ),
    );
  }

  function labelled(label, control) {
    return h('label', { style: { display: 'flex', alignItems: 'center', gap: '7px', font: '500 11px/1 var(--font)', color: 'var(--tx-4)' } }, label, control);
  }

  function refreshSetup() {
    if (G) return;
    const next = renderSetup();
    els.body.replaceWith(next);
    els.body = next;
  }

  /* ------------------------------------------------------------------ */
  /* scenario picker                                                     */
  /* ------------------------------------------------------------------ */

  /**
   * Fetch the catalogue once, the first time anything needs it.
   *
   * Not part of boot. The card database and the meta snapshot are what the app
   * cannot open without; a puzzle list is not, and the day this comes from an
   * authenticated endpoint it must not be able to hold up the deck builder.
   */
  function ensureScenarios() {
    if (scenariosRequested) return;
    scenariosRequested = true;
    loadScenarios().then(() => {
      scenariosLoaded = true;
      /* A failed fetch is not an answer — re-arm, so coming back to the tab
       * tries again rather than showing the same stale apology all session. */
      if (scenariosError()) scenariosRequested = false;
      /* Pick the interrupted puzzle back up. Only the id was kept, so this is
       * the same board from the top — see `data/scenarios.js` for why
       * replaying the moves back would be a worse answer than restarting. */
      const resume = currentScenarioId();
      if (resume && !G) {
        const sc = scenarioById(resume);
        if (sc) { startScenario(sc); return; }
        setCurrentScenarioId(null);   // it was removed from the catalogue
      }
      if (!G) refreshSetup();
    });
  }

  function renderScenarioPicker() {
    const db = getCards();
    const list = getScenarios();
    const err = scenariosError();

    return h('div.panel', { style: { padding: '20px' } },
      h('h2', { style: { margin: '0 0 4px', font: '600 17px/1 var(--font)' } }, 'Scenarios'),
      h('div', { style: { font: '400 12px/1.6 var(--font)', color: 'var(--tx-4)', marginBottom: '18px' } },
        'A board from the middle of a game, and something to achieve from it. '
        + 'Every one has a line that works — finding it is the puzzle. Retry as often as you like.'),

      !scenariosLoaded
        ? h('div.empty-note', 'Loading scenarios…')
        : err
          ? h('div.empty-note', `Could not load the scenarios: ${err}`)
          : !list.length
            ? h('div.empty-note', 'No scenarios yet.')
            : h('div.sclist', list.map(scenarioRow(db))),
    );
  }

  const scenarioRow = db => sc => {
    const missing = missingCards(sc, db);
    const solved = isSolved(sc.id);
    return h('button.scrow', {
      type: 'button',
      class: [solved ? 'is-solved' : '', missing.length ? 'is-blocked' : ''].filter(Boolean).join(' '),
      disabled: missing.length > 0,
      onclick: () => startScenario(sc),
    },
      h('div.scrow-h',
        h('div.scname', sc.name),
        difficultyDots(sc.difficulty),
        solved ? h('span.scdone', 'SOLVED') : null,
      ),
      h('div.scbrief', sc.brief),
      h('div.scgoal', missing.length
        /* Named, not hidden. A puzzle that vanishes looks like it was never
         * written; a puzzle that says which card the dump is missing is a bug
         * report someone can act on. */
        ? `Unavailable — the card data is missing ${missing.join(', ')}`
        : goalLabel(sc.goal)),
    );
  };

  /* ------------------------------------------------------------------ */
  /* strip + alerts                                                      */
  /* ------------------------------------------------------------------ */

  function renderStrip() {
    const yourTurn = G.active === HUMAN;
    return h('div.bd-strip',
      h('div.bd-strip-vs',
        h('b', G.p1.name || 'Your deck'), h('span', ' vs '), G.p2.name),
      h('div.spacer'),
      h('div.bd-strip-turn', G.over
        ? 'Game over'
        : `Turn ${G.turn} · ${yourTurn ? 'your' : 'their'} ${PHASE_LABEL[G.phase]} Phase`),
    );
  }

  function renderAlerts() {
    const out = [];
    /* The puzzle's own banner replaces the win/lose one. "You lose" is the
     * wrong sentence for a scenario — nothing was lost, a line was missed, and
     * the board is one button away from being set up again. */
    if (scenario) out.push(renderScenarioBanner());
    else if (G.over) out.push(renderOver());
    if (attacker && !G.over) {
      out.push(h('div.bd-alert',
        h('div.bd-alert-h', `${unitName(G, HUMAN, attacker)} is attacking — pick a target on their mat`),
        h('div.bd-alert-row',
          h('button.btn', { onclick: () => { attacker = null; render(); } }, 'Cancel')),
      ));
    }
    /* A decided puzzle's leftover prompt is not answerable — `act` refuses it
     * — so it is not offered either. */
    if (G.pending && G.pending.side === HUMAN && (!scenario || scenarioStatus === 'open')) {
      out.push(renderPrompt(G.pending));
    }
    return h('div.bd-alerts', out);
  }

  /* ------------------------------------------------------------------ */
  /* the puzzle, while it is being solved                                */
  /* ------------------------------------------------------------------ */

  function renderScenarioBanner() {
    const solved = scenarioStatus === 'solved';
    const failed = scenarioStatus === 'failed';
    const done = solved || failed;

    return h('div.bd-alert', { class: solved ? 'is-win' : (failed ? 'is-lose' : '') },
      h('div.bd-alert-h',
        solved ? `Solved — ${scenario.name}` : failed ? `Not this time — ${scenario.name}` : scenario.name),

      h('div.bd-alert-b', solved
        /* The lesson is the payoff, which is why it is withheld until now: a
         * puzzle that explains itself up front is a worked example, and the
         * player was asked to work it out. */
        ? (scenario.lesson || 'That is the line.')
        : failed
          ? failureNote()
          : `${scenario.brief}\n${goalLabel(scenario.goal)}`),

      hint && !done ? h('div.bd-alert-b', { class: 'is-hint' }, hint) : null,

      h('div.bd-alert-row',
        h(done ? 'button.btn-pri' : 'button.btn', {
          onclick: () => retryScenario(),
        }, solved ? 'Play it again' : 'Retry'),
        done ? null : h('button.btn', { onclick: () => showHint() }, 'Hint'),
        h('button.btn', { onclick: () => leaveScenario() }, 'All scenarios'),
      ),
    );
  }

  function failureNote() {
    if (G.over && G.over.winner === AI) return G.over.reason;
    return 'The turn passed with the goal unmet. The board resets exactly as it was — retry costs nothing.';
  }

  /**
   * A nudge, not the answer.
   *
   * `pressure()` reports what is on the table against what is needed. "You
   * have enough power, you are spending it wrong" is a useful thing to be
   * told; the line is not, and a hint that gives it away turns the puzzle into
   * a replay.
   */
  function showHint() {
    const p = pressure(G, HUMAN);
    const facts = `They are on ${p.lifeLeft} Life behind a ${fmt(p.leaderPower)} Leader. `
      + `You have ${p.swings} attack${p.swings === 1 ? '' : 's'} left this turn; `
      + `${p.connecting} would beat that number as things stand.`;
    const nudge = p.swings === 0
      ? 'Nothing is left to attack with — the answer had to come earlier in the turn.'
      : p.connecting === 0
        ? 'Nothing you have swings big enough yet. Something has to change before you attack.'
        : 'You have power that connects. If it is still not enough, ask what is standing in the way.';
    hint = `${facts} ${nudge}`;
    announce(hint);
    render();
  }

  function renderOver() {
    const won = G.over.winner === HUMAN;
    return h('div.bd-alert', { class: won ? 'is-win' : 'is-lose' },
      h('div.bd-alert-h', won ? 'You win' : 'You lose'),
      h('div.bd-alert-b', G.over.reason),
      h('div.bd-alert-row',
        // Cancel the pending AI step too — reset() does, and this must match.
        h('button.btn-pri', {
          onclick: () => { clearTimeout(pendingAiTimer); G = null; render(); },
        }, 'New game')),
    );
  }

  function renderPrompt(p) {
    const opts = [];

    if (p.type === 'block') {
      opts.push(h('button.btn-pri', { onclick: () => act({ type: 'block', uid: null }) }, 'No block'));
      for (const o of p.options) {
        opts.push(h('button.btn', { onclick: () => act({ type: 'block', uid: o.uid }) },
          `Block with ${o.label} (${fmt(o.power)})`));
      }
    } else if (p.type === 'counter') {
      const gap = p.attackPower - p.defendPower;
      opts.push(h('button.btn-pri', { onclick: () => act({ type: 'counter', index: null }) },
        gap >= 0 ? 'Take the hit' : 'Done'));
      for (const o of p.options) {
        opts.push(h('button.btn', { onclick: () => act({ type: 'counter', index: o.index }) },
          `${o.label} +${fmt(o.value)}${o.cost ? ` (${o.cost} DON!!)` : ''}`));
      }
    } else if (p.type === 'trigger') {
      opts.push(h('button.btn-pri', { onclick: () => act({ type: 'trigger', activate: true }) }, 'Activate'));
      opts.push(h('button.btn', { onclick: () => act({ type: 'trigger', activate: false }) }, 'Keep in hand'));
    } else if (p.type === 'confirm') {
      opts.push(h('button.btn-pri', { onclick: () => act({ type: 'choose', value: true }) }, 'Pay it'));
      opts.push(h('button.btn', { onclick: () => act({ type: 'choose', value: false }) }, 'Decline'));
    } else if (p.type === 'manual') {
      for (const o of p.options) {
        opts.push(h('button.btn', { onclick: () => act({ type: 'choose', value: o.key }) }, o.label));
      }
    } else if (p.type === 'target') {
      if (p.optional) opts.push(h('button.btn', { onclick: () => act({ type: 'choose', value: null }) }, 'Skip'));
      for (const o of p.options) {
        opts.push(h('button.btn-pri', {
          onclick: () => act({ type: 'choose', value: o.uid ?? o.index }),
        }, o.label));
      }
    }

    const detail = p.type === 'counter'
      ? `Attack ${fmt(p.attackPower)} vs your ${fmt(p.defendPower)} — you need ${fmt(Math.max(0, p.attackPower - p.defendPower + 1))} more to survive.`
      : (p.text || '');

    return h('div.bd-alert.anim-pop',
      h('div.bd-alert-h', p.prompt),
      detail ? h('div.bd-alert-b', detail) : null,
      h('div.bd-alert-row', opts),
    );
  }

  /* ------------------------------------------------------------------ */
  /* mat pieces                                                          */
  /* ------------------------------------------------------------------ */

  /** Face-down backs, used for the opponent's hand. */
  const backs = n => Array.from({ length: n }, () => h('div.bd-back'));

  function donDeckCell(count, { onclick } = {}) {
    return h('div.bd-cell.at-dondeck',
      h('div.bd-dondeck', {
        title: `${count} DON!! left in the DON!! deck`,
        style: onclick ? { cursor: 'pointer' } : null,
        onclick,
      },
        h('div.bd-dondeck-n', String(count)),
        h('div.bd-dondeck-cap', 'DON!! DECK'),
      ),
    );
  }

  /** The zone's name — centred while it is empty, out of the way once it isn't. */
  const zoneLabel = (text, occupied) =>
    h('div.bd-zone-label', { class: occupied ? 'is-tucked' : '' }, text);

  /** The cost area: DON!! stand up when active and lie down when spent. */
  function costZone(active, rested) {
    return h('div.bd-zone.at-cost', {
      role: 'img',
      'aria-label': `Cost area: ${active} active DON!!, ${rested} rested`,
    },
      zoneLabel('Cost', active + rested > 0),
      h('div.bd-zone-body',
        Array.from({ length: active }, () => h('div.bd-donchip', { title: 'Active DON!!' })),
        Array.from({ length: rested }, () => h('div.bd-donchip.is-rested', { title: 'Rested DON!!' })),
      ),
    );
  }

  function discardZone(count) {
    return h('div.bd-zone.is-dim.at-discard',
      h('div.bd-zone-label', 'Discard'),
      count ? h('div.bd-zone-n', String(count)) : null,
    );
  }

  /**
   * Life, as a stack of face-down cards with the count on the top one.
   *
   * `flip` puts the stack the other way up for the player, so both read as
   * "cards left between them and losing" from where each player sits.
   */
  function lifeCell(life, lifeMax, flip) {
    const bars = Math.max(0, life - 1);
    const cap = h('div.bd-cap', 'LIFE');
    const stack = h('div.bd-life', {
      role: 'img',
      'aria-label': `${flip ? 'Your' : 'Opponent'} life: ${life} of ${lifeMax}`,
      style: { height: `${bars * 20 + 90}px` },
    },
      ...(life
        ? [
          ...Array.from({ length: bars }, (_, i) => h('div.bd-life-bar', {
            style: flip ? { bottom: `${i * 20}px` } : { top: `${i * 20}px` },
          })),
          h('div.bd-life-top', {
            style: flip ? { bottom: `${bars * 20}px` } : { top: `${bars * 20}px` },
          }, h('div.bd-num', String(life))),
        ]
        : [h('div.bd-num', { style: { margin: 'auto' } }, '0')]),
    );
    return h('div.bd-cell.at-life', ...(flip ? [cap, stack] : [stack, cap]));
  }

  function deckCell(count) {
    return h('div.bd-cell.at-deck',
      h('div.bd-deck', { title: `${count} cards left in deck` },
        h('i'), h('i'), h('i', h('div.bd-num', String(count)))),
      h('div.bd-cap', 'DECK'),
    );
  }

  function leaderRow(tile, stageTile) {
    return h('div.bd-leaderrow.at-leader', tile, stageTile);
  }

  function charsZone(tiles) {
    return h('div.bd-zone.bd-chars.at-chars',
      zoneLabel('Characters', tiles.length > 0),
      tiles.length ? h('div.bd-zone-body', tiles) : null,
    );
  }

  /* ------------------------------------------------------------------ */
  /* the two mats                                                        */
  /* ------------------------------------------------------------------ */

  function renderOpponentMat(db, opp) {
    const targets = attacker ? attackTargets(G, HUMAN, attacker) : [];
    const targetLeader = targets.some(t => t.kind === 'leader');
    const targetChars = new Set(targets.filter(t => t.kind === 'char').map(t => t.uid));

    const strike = target => () => {
      const a = attacker;
      attacker = null;
      act({ type: 'attack', attacker: a, target });
    };

    return h('div.bd-mat',
      donDeckCell(opp.donDeck),
      costZone(opp.donActive, opp.donRested),
      discardZone(opp.trash),

      lifeCell(opp.life, opp.lifeMax, false),
      leaderRow(
        h('div.bd-slot', cardTile(db[opp.leaderId], {
          power: opp.leaderPower,
          pips: opp.leaderDon,
          badge: null,
          rested: opp.leaderRested,
          target: !!attacker && targetLeader,
          onClick: attacker && targetLeader ? strike({ kind: 'leader' }) : undefined,
          title: `${G.p2.name} — Leader`,
        })),
        opp.stage
          ? h('div.bd-slot', cardTile(db[opp.stage], { badge: 'STAGE' }))
          : h('div.bd-slot-empty', 'Stage'),
      ),
      deckCell(opp.deck),

      charsZone(opp.chars.map(ch => {
        const isTarget = !!attacker && targetChars.has(ch.uid);
        return h('div.bd-slot', cardTile(db[ch.id], {
          power: ch.power, pips: ch.don, rested: ch.rested, target: isTarget,
          badge: ch.rested ? 'REST' : null,
          onClick: isTarget ? strike({ kind: 'char', uid: ch.uid }) : undefined,
          title: ch.rested
            ? `${db[ch.id]?.name} (rested — attackable)`
            : `${db[ch.id]?.name} (active — cannot be attacked)`,
        }));
      })),
    );
  }

  function renderPlayerMat(db, me) {
    return h('div.bd-mat.is-player',
      charsZone(me.chars.map(ch => h('div.bd-slot', cardTile(db[ch.id], {
        power: ch.power, pips: ch.don, rested: ch.rested,
        badge: ch.rested ? 'REST' : (ch.summoningSick ? 'SICK' : null),
        selected: selected?.uid === ch.uid || attacker === ch.uid,
        onClick: () => { selected = { kind: 'char', uid: ch.uid }; attacker = null; render(); },
        title: db[ch.id]?.name,
      })))),

      lifeCell(me.life, me.lifeMax, true),
      leaderRow(
        h('div.bd-slot', cardTile(db[me.leaderId], {
          power: me.leaderPower, pips: me.leaderDon, badge: null,
          rested: me.leaderRested,
          selected: selected?.kind === 'leader' || attacker === 'leader',
          onClick: () => { selected = { kind: 'leader' }; attacker = null; render(); },
          title: `${G.p1.name} — Leader`,
        })),
        me.stage
          ? h('div.bd-slot', cardTile(db[me.stage], { badge: 'STAGE' }))
          : h('div.bd-slot-empty', 'Stage'),
      ),
      deckCell(me.deck),

      donDeckCell(me.donDeck),
      costZone(me.donActive, me.donRested),
      discardZone(me.trash),
    );
  }

  /* ------------------------------------------------------------------ */
  /* hand + the two table actions                                        */
  /* ------------------------------------------------------------------ */

  function renderHandRow(db, me, playableIdx) {
    const n = me.hand.length;
    const cards = me.hand.map((id, i) => {
      /* A fan, centred. Written as a custom property rather than a transform
       * so the stylesheet keeps ownership of `transform` and :hover can
       * straighten the card without an inline style outranking it. */
      const rot = n > 1 ? ((i - (n - 1) / 2) * 2.2).toFixed(2) : '0';
      const playable = playableIdx.has(i);
      const el = h('button.bd-hand-card.anim-deal', {
        class: [
          selected?.kind === 'hand' && selected.index === i ? 'is-selected' : '',
          !playable && G.active === HUMAN && G.phase === 'main' ? 'is-dim' : '',
        ].filter(Boolean).join(' '),
        title: db[id]?.name,
        onclick: () => { selected = { kind: 'hand', index: i }; attacker = null; render(); },
      }, cardTile(db[id], { hoverable: false }));
      el.style.setProperty?.('--rot', `${rot}deg`);
      return el;
    });

    const phaseIdx = PHASES.indexOf(G.phase);
    const yourTurn = G.active === HUMAN;
    const canAdvance = yourTurn && !G.pending && !G.over;
    const advanceLabel = G.over ? 'Game over'
      : G.phase === 'end' ? 'End turn'
        : `Advance to ${PHASE_LABEL[PHASES[phaseIdx + 1]]}`;

    /* The selected unit, if it is one and it can swing. The mockup made
     * "Declare Attack" the gold button; here the gold one is whatever moves
     * the turn forward, because that is the button you need every single
     * phase and the one that must never be ambiguous. Attack is armed
     * alongside it. */
    const ref = selected?.kind === 'leader' ? 'leader' : selected?.kind === 'char' ? selected.uid : null;
    /* Having a legal target is part of being able to swing. [Rush: Character]
     * can pass `canAttack` with nothing it is allowed to hit, and arming an
     * attack that then highlights nothing is a dead end the player has to
     * back out of. */
    const armed = ref != null && canAttack(G, HUMAN, ref) && attackTargets(G, HUMAN, ref).length > 0;

    return h('div.bd-handrow',
      h('div.bd-hand', cards.length ? cards : h('div.bd-hand-empty', 'Your hand is empty.')),
      h('div.bd-actions',
        h('button.btn-pri.bd-btn.bd-btn-gold', {
          disabled: !canAdvance,
          onclick: () => act({ type: 'advance' }),
        }, advanceLabel),
        h('button.bd-btn', {
          class: armed ? 'is-armed' : '',
          disabled: !armed,
          title: armed ? '' : 'Select one of your Characters or your Leader that can attack',
          onclick: () => { attacker = ref; selected = null; render(); },
        }, 'Declare Attack'),
      ),
    );
  }

  /* ------------------------------------------------------------------ */
  /* inspector rail                                                      */
  /* ------------------------------------------------------------------ */

  function renderInspector(db, me) {
    if (!selected) {
      return h('div.bd-insp-empty',
        'Click a card in hand to play it, or one of your Characters to attack with it.');
    }

    let card = null;
    let actions = null;

    if (selected.kind === 'hand') {
      const id = me.hand[selected.index];
      card = db[id];
      if (!card) return h('div.bd-insp-empty', 'That card is no longer in hand.');
      const playable = legalActions(G).some(a => a.type === 'play' && a.index === selected.index);
      const why = G.active !== HUMAN ? 'Not your turn'
        : G.phase !== 'main' ? 'Only during your Main Phase'
          : costOf(card) > me.donActive ? `Needs ${costOf(card)} active DON!!`
            : (card.category === 'Character' && me.chars.length >= 5) ? 'Character area is full'
              : 'Play';
      actions = h('div.bd-acts',
        h('button.bd-btn.bd-btn-gold', {
          disabled: !playable,
          onclick: () => act({ type: 'play', index: selected.index }),
        }, playable ? `Play for ${costOf(card)} DON!!` : why),
      );
    } else {
      const ref = selected.kind === 'leader' ? 'leader' : selected.uid;
      const id = selected.kind === 'leader' ? me.leaderId : me.chars.find(c => c.uid === ref)?.id;
      card = db[id];
      if (!card) return h('div.bd-insp-empty', 'That card has left the field.');

      const canSwing = canAttack(G, HUMAN, ref);
      const targets = attackTargets(G, HUMAN, ref);
      const able = canSwing && targets.length > 0;
      // Two different "no" answers, and the player can act on only one of them.
      const noAttackWhy = canSwing ? 'No legal target' : 'Cannot attack';
      const ability = selected.kind === 'leader'
        ? leaderAbilityStatus(G, HUMAN)
        : charAbilityStatus(G, HUMAN, ref);
      /* `!!G.pending` matches the advance button; without it this stayed live
       * during a block, counter or target prompt. */
      const canAttach = !G.pending && me.donActive >= 1 && G.active === HUMAN && G.phase === 'main';

      actions = h('div.bd-acts',
        h('button.bd-btn.bd-btn-gold', {
          disabled: !able,
          onclick: () => { attacker = ref; selected = null; render(); },
        }, able ? 'Declare attack' : noAttackWhy),
        h('button.bd-btn', {
          disabled: !canAttach,
          onclick: () => act({ type: 'attach', target: ref }),
        }, 'Attach 1 DON!! (+1000)'),
        /* Stays put and explains itself when unusable — a control that
         * disappears reads as "this card has no ability at all". */
        ability.has ? h('button.bd-btn', {
          disabled: !ability.usable,
          title: ability.usable ? '' : ability.reason,
          /* The Leader's ability takes no uid — there is only one Leader — and
           * the engine's action shapes are matched exactly rather than sent a
           * superset and trusted to ignore the extra. */
          onclick: () => act(selected.kind === 'leader'
            ? { type: 'leaderAbility' }
            : { type: 'activateChar', uid: ref }),
        }, 'Use ability') : null,
      );
    }

    const meta = [card.category, (card.types || []).join('/'), card.id].filter(Boolean).join(' · ');

    return h('div.anim-pop',
      h('div.bd-insp-art', cardTile(card, { hoverable: false })),
      h('div.bd-insp-name', card.name),
      h('div.bd-insp-meta', meta),
      h('div.bd-tri',
        h('div', h('div.v.is-gold', costLabel(card)), h('div.k', 'COST')),
        h('div', h('div.v', card.power != null ? fmt(card.power) : '—'), h('div.k', 'POWER')),
        h('div', h('div.v', card.counter ? fmt(card.counter) : '—'), h('div.k', 'COUNTER')),
      ),
      card.effect && card.effect !== '-'
        ? h('div.bd-insp-text', String(card.effect).replace(/<br>/g, '\n'))
        : null,
      card.trigger
        ? h('div.bd-insp-text.is-trigger', String(card.trigger).replace(/<br>/g, '\n'))
        : null,
      actions,
      h('div.bd-acts',
        h('button.bd-btn', { onclick: () => { selected = null; render(); } }, 'Deselect')),
    );
  }

  function renderPhaseRow() {
    const phaseIdx = PHASES.indexOf(G.phase);
    const yourTurn = G.active === HUMAN;
    return h('div.bd-phase', PHASES.map((p, i) => {
      const rule = ruleFor(p);
      return h('div', {
        class: i === phaseIdx && yourTurn ? 'is-on' : (i < phaseIdx ? 'is-done' : ''),
        title: rule ? `${rule.title}: ${rule.body}` : '',
      }, PHASE_LABEL[p].toUpperCase());
    }));
  }

  function renderLog() {
    const entries = G.log.slice(-80).reverse();
    const info = explain(G.log[explainIdx >= 0 ? explainIdx : G.log.length - 1], { beginner });

    return h('div.bd-sec-scroll',
      h('div.bd-loghead',
        h('div.bd-sec-h', 'GAME LOG'),
        h('label.bd-teach', { title: 'Longer explanations aimed at new players' },
          h('input', { type: 'checkbox', checked: beginner, onchange: e => { beginner = e.target.checked; render(); } }),
          'teach'),
      ),
      h('div.bd-log',
        entries.map((e, i) => h('button.bd-log-row', {
          class: [i < 2 ? 'is-recent' : '', explainIdx === e.i ? 'is-on' : ''].filter(Boolean).join(' '),
          onclick: () => { explainIdx = e.i; explainOpen = true; render(); },
        },
          h('span.bd-log-t', `T${e.turn}`),
          h('span.bd-log-x', e.text),
        )),
        !entries.length ? h('div.bd-insp-empty', 'No events yet.') : null,
      ),
      h('div.bd-acts',
        h('button.bd-btn', {
          onclick: () => { explainIdx = G.log.length - 1; explainOpen = true; render(); },
        }, 'Explain what just happened'),
      ),
      explainOpen ? h('div.bd-explain',
        h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: '9px' } },
          h('h4', { style: { flex: '1' } }, info.title),
          h('button.bd-x', {
            'aria-label': 'Close explanation',
            onclick: () => { explainOpen = false; render(); },
          }, '×'),
        ),
        h('div.body', info.body),
        h('div.rule', h('div.k', 'RULE'), h('div.v', info.rule)),
      ) : null,
    );
  }

  function renderSide(db, me) {
    return h('aside.bd-side',
      h('div.bd-sec',
        h('div.bd-sec-h', 'SELECTED'),
        renderInspector(db, me),
      ),
      h('div.bd-sec',
        h('div.bd-sec-h', 'PHASE'),
        renderPhaseRow(),
      ),
      renderLog(),
    );
  }

  /* ------------------------------------------------------------------ */
  /* fit-to-pane                                                         */
  /* ------------------------------------------------------------------ */

  /* The mat is drawn at MAT_W and scaled down to whatever the pane has, so
   * every card, gap and label stays in proportion at any window size — which
   * a fluid layout would not. Measured after layout, not guessed. */
  function applyFit() {
    const box = root.querySelector?.('.bd-fit');
    const wrap = root.querySelector?.('.bd-fitwrap');
    const stack = root.querySelector?.('.bd-stack');
    if (!box || !wrap || !stack) return;

    stack.style.transform = 'none';
    wrap.style.height = 'auto';
    const natW = stack.offsetWidth || MAT_W;
    const natH = stack.offsetHeight;
    if (!natH) return;

    const byWidth = (box.clientWidth - 20) / natW;
    const byHeight = (box.clientHeight - 8) / natH;
    /* Width is a hard limit — overflowing sideways puts half the mat off
     * screen with no way back. Height is not: past MIN_FIT the box scrolls. */
    const s = Math.min(byWidth, Math.max(byHeight, MIN_FIT), 1);
    stack.style.transformOrigin = 'top center';
    stack.style.transform = `scale(${s})`;
    wrap.style.height = `${natH * s}px`;
  }

  let fitObs = null;
  function watchFit() {
    if (typeof ResizeObserver === 'undefined') return;
    const box = root.querySelector?.('.bd-fit');
    if (!box) { fitObs?.disconnect(); return; }
    /* The observed node is replaced on every render, so re-point rather than
     * accumulating observations on detached elements. */
    fitObs ??= new ResizeObserver(() => applyFit());
    fitObs.disconnect();
    fitObs.observe(box);
  }

  /* ------------------------------------------------------------------ */
  /* rules drawer                                                        */
  /* ------------------------------------------------------------------ */

  function renderDrawer() {
    if (!helpOpen) return null;
    return h('div.drawer',
      h('div', { style: { display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '11px' } },
        h('h3', { style: { flex: '1' } }, 'Turn structure'),
        h('button', {
          style: { border: '0', background: 'none', color: 'var(--tx-4)', font: '400 16px/1 var(--font)' },
          'aria-label': 'Close', onclick: () => { helpOpen = false; render(); },
        }, '×'),
      ),
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
        RULES.map(r => h('div', { style: { display: 'flex', gap: '10px' } },
          h('div', { style: { width: '16px', flex: 'none', font: '700 10px/1.6 var(--mono)', color: 'var(--accent)' } }, r.n),
          h('div', { style: { flex: '1' } },
            h('div', { style: { font: '600 11.5px/1.4 var(--font)', color: '#dbe0ea' } }, r.title),
            h('div', { style: { font: '400 10.5px/1.5 var(--font)', color: 'var(--tx-3)', marginTop: '2px' } }, r.body),
          ),
        ))),
    );
  }

  /* ------------------------------------------------------------------ */
  /* assembly                                                            */
  /* ------------------------------------------------------------------ */

  function renderTable() {
    const db = G.cards || getCards();
    const me = view(G, HUMAN);
    const opp = view(G, AI);

    /* One enumeration per render, not one per card in hand.
     *
     * This was called inside the hand loop, where the result cannot change.
     * render() runs on every click, on every AI step at 420ms and on every log
     * interaction, so a ten-card hand meant ten full legal-action
     * enumerations per frame. */
    const playableIdx = new Set(
      legalActions(G).filter(a => a.type === 'play').map(a => a.index));

    return h('div.bd',
      h('div.bd-main',
        renderStrip(),
        renderAlerts(),
        h('div.bd-fit',
          h('div.bd-fitwrap',
            h('div.bd-stack',
              h('div.bd-ohand', {
                role: 'img',
                'aria-label': `Opponent hand: ${opp.handCount} cards`,
              }, opp.handCount
                ? backs(Math.min(opp.handCount, 10))
                : h('div.bd-ohand-note', 'Their hand is empty.')),
              renderOpponentMat(db, opp),
              renderPlayerMat(db, me),
              renderHandRow(db, me, playableIdx),
            ),
          ),
        ),
      ),
      renderSide(db, me),
    );
  }

  /* An unsolved puzzle survives a reload: the id was remembered, so the tab
   * opens on it rather than on the free-play form. Solving it or leaving it
   * clears that, so a finished scenario does not capture the tab for good. */
  if (currentScenarioId()) {
    setupMode = 'scenarios';
    ensureScenarios();
  }

  els.body = renderSetup();
  els.drawer = h('div');
  const root = h('div.view', { id: 'view-board' }, els.body, els.drawer);

  function render() {
    const next = G ? renderTable() : renderSetup();
    els.body.replaceWith(next);
    els.body = next;
    fill(els.drawer, renderDrawer());

    if (G) {
      watchFit();
      applyFit();
      /* Web fonts land after first paint and change the mat's natural height,
       * so the first measurement is stale by a few pixels. */
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(applyFit);
    }

    /* Announce a prompt once, not on every render. render() runs when the
     * teach checkbox is toggled, a log entry is clicked, the explanation is
     * closed or a card is selected — so an open prompt was repeated into the
     * screen reader on every unrelated interaction. */
    const prompt = G?.pending?.side === HUMAN ? G.pending.prompt : null;
    if (prompt && prompt !== announcedPrompt) announce(prompt);
    announcedPrompt = prompt;
  }

  return {
    root,
    render() { render(); },
    toggleHelp() { helpOpen = !helpOpen; render(); },
    reset() { clearTimeout(pendingAiTimer); G = null; scenario = null; clearTable(); render(); },
    isPlaying: () => !!G,
    start: startGame,
    startScenario,
  };
}
